import {
  Button,
  Icon,
  Offscreen,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from '@deque/cauldron-react';
import React from 'react';
import { useTranslation } from 'react-i18next';
import useElementHeight from '~/common/hooks/useElementHeight';
import type { SecurityEvent } from '~/common/utils/admin-client/get-security-event-log';
import styles from './SecurityEventLogTable.css';
import { formatDate, getCategoryLabel, getEventTypeLabel } from './utils';

interface SecurityEventLogTableProps {
  events: SecurityEvent[];
  onViewEvent: (event: SecurityEvent) => void;
}

const SecurityEventLogTable: React.FC<SecurityEventLogTableProps> = ({
  events,
  onViewEvent
}) => {
  const { t } = useTranslation();
  const { ref: headRef, height: headerHeight } =
    useElementHeight<HTMLTableSectionElement>();

  return (
    <div
      className={styles.scrollContainer}
      role="region"
      aria-label={t('Security events')}
      tabIndex={0}
      style={
        {
          '--sticky-header-height': `${headerHeight}px`
        } as React.CSSProperties
      }
    >
      <Table variant="border">
        <TableHead ref={headRef}>
          <TableRow>
            <TableHeader scope="col">{t('Timestamp')}</TableHeader>
            <TableHeader scope="col">{t('Enterprise')}</TableHeader>
            <TableHeader scope="col">{t('Event')}</TableHeader>
            <TableHeader scope="col">{t('Category')}</TableHeader>
            <TableHeader scope="col">{t('Actor')}</TableHeader>
            <TableHeader scope="col">{t('IP Address')}</TableHeader>
            <TableHeader scope="col">{t('Admin')}</TableHeader>
            <TableHeader scope="col">{t('Details')}</TableHeader>
          </TableRow>
        </TableHead>
        <TableBody>
          {events.length === 0 ? (
            <TableRow>
              <TableCell colSpan={8}>
                <div className={styles.empty}>
                  {t('No events found. Try adjusting your filters.')}
                </div>
              </TableCell>
            </TableRow>
          ) : (
            events.map(event => {
              const timestamp = formatDate(event.timestamp);
              return (
                <TableRow key={event.event_id}>
                  <TableCell>{timestamp}</TableCell>
                  <TableCell>
                    {event.enterprise_name ? (
                      <>
                        {event.enterprise_name}
                        <br />
                        <span className={styles.muted}>
                          <code>{event.enterprise_id}</code>
                        </span>
                      </>
                    ) : event.enterprise_id ? (
                      <code className={styles.muted}>
                        {event.enterprise_id}
                      </code>
                    ) : (
                      <span className={styles.muted}>&mdash;</span>
                    )}
                  </TableCell>
                  <TableCell>
                    <span className={styles.eventType}>
                      {getEventTypeLabel(t, event.event_type)}
                    </span>
                  </TableCell>
                  <TableCell>{getCategoryLabel(t, event.category)}</TableCell>
                  <TableCell>
                    {event.actor_display_name || (
                      <span className={styles.muted}>{t('System')}</span>
                    )}
                  </TableCell>
                  <TableCell>
                    {event.actor_ip_address ? (
                      <code>{event.actor_ip_address}</code>
                    ) : (
                      <span className={styles.muted}>&mdash;</span>
                    )}
                  </TableCell>
                  <TableCell>
                    {event.admin_event ? (
                      <Icon
                        type="check-circle"
                        className={styles.adminIcon}
                        label={t('Admin event')}
                      />
                    ) : (
                      <>
                        <span className={styles.muted} aria-hidden="true">
                          &mdash;
                        </span>
                        <Offscreen>{t('Not an admin event')}</Offscreen>
                      </>
                    )}
                  </TableCell>
                  <TableCell>
                    <Button
                      variant="secondary"
                      thin
                      aria-label={t('View {{event}} event at {{timestamp}}', {
                        event: event.event_type,
                        timestamp
                      })}
                      onClick={() => onViewEvent(event)}
                    >
                      {t('View')}
                    </Button>
                  </TableCell>
                </TableRow>
              );
            })
          )}
        </TableBody>
      </Table>
    </div>
  );
};

export default SecurityEventLogTable;
